import { IconBook, IconClock, IconPlus } from '@tabler/icons-react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getCourses } from '../../store/courseSlice';
import { getLessons } from '../../store/lessonSlice';
import { getPrograms } from '../../store/programSlice';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import BreadcrumbNav from './BreadcrumbNav';

export default function CourseLessons() {
    const { courseId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const courses = useSelector(state => state.course.list);
    const programs = useSelector(state => state.program.list);
    const lessons = useSelector(state => state.lesson.list);
    const loading = useSelector(state => state.lesson.loading);
    
    const course = courses.find(c => c.id === parseInt(courseId));
    const program = course ? programs.find(p => p.id === course.program_id) : null;
    const courseLessons = lessons.filter(l => l.courseId === parseInt(courseId));

    useEffect(() => {
        dispatch(getCourses());
        dispatch(getPrograms());
        dispatch(getLessons(courseId));
    }, [dispatch, courseId]);

    const handleAddLesson = () => {
        navigate(`/curriculum/courses/${courseId}/lessons/new`);
    };

    const breadcrumbItems = [
        { label: 'Curriculum', href: '/curriculum' },
        { label: program?.name || 'Program', href: `/curriculum/programs/${program?.id}/courses` },
        { label: course?.name || 'Course', href: `/curriculum/courses/${courseId}/lessons` }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">Loading lessons...</div>
            </div>
        );
    }

    if (!course) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">Course not found</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <BreadcrumbNav items={breadcrumbItems} />
            
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">{course.name}</h1>
                    <p className="text-muted-foreground">
                        {course.description || 'No description available'}
                    </p>
                </div>
                <Button onClick={handleAddLesson}>
                    <IconPlus className="mr-2 h-4 w-4" />
                    Add Lesson
                </Button>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {courseLessons.map((lesson) => (
                    <Card 
                        key={lesson.id} 
                        className="h-full cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-1 border-border hover:border-primary/20"
                    >
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <IconBook className="h-5 w-5 text-primary" />
                                {lesson.name}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-muted-foreground mb-4">
                                {lesson.content ? 
                                    lesson.content.substring(0, 100) + (lesson.content.length > 100 ? '...' : '') 
                                    : 'No content available'
                                }
                            </p>
                            <div className="flex items-center justify-between text-sm text-muted-foreground">
                                <div className="flex items-center gap-1">
                                    <IconClock className="h-4 w-4" />
                                    <span>{lesson.difficulty || 'Standard'}</span>
                                </div>
                                <Link 
                                    to={`/curriculum/lessons/${lesson.id}`}
                                    className="text-primary hover:text-primary/80 hover:underline"
                                >
                                    View Details →
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>


        </div>
    );
} 